import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  Volume2,
  Type,
  Keyboard,
  Monitor,
  Smartphone,
  CheckCircle,
  Settings,
  MousePointer,
} from 'lucide-react';

function AccessibilityPage() {
  const { t, i18n } = useTranslation();

  const accessibilityFeatures = [
    {
      icon: Type,
      title:
        i18n.language === 'mr' ? 'अक्षर आकार नियंत्रण' : 'Font Size Control',
      description:
        i18n.language === 'mr'
          ? 'मजकूराचा आकार वाढवण्यासाठी किंवा कमी करण्यासाठी A+, A=, A-, A बटणांचा वापर करा'
          : 'Use A+, A=, A-, A buttons to increase or decrease text size for better readability',
    },
    {
      icon: Volume2,
      title: i18n.language === 'mr' ? 'भाषा समर्थन' : 'Language Support',
      description:
        i18n.language === 'mr'
          ? 'वेबसाइट मराठी आणि इंग्रजी या दोन्ही भाषांमध्ये उपलब्ध आहे'
          : 'Website is available in both Marathi and English languages',
    },
    {
      icon: Keyboard,
      title:
        i18n.language === 'mr' ? 'कळफलक नेव्हिगेशन' : 'Keyboard Navigation',
      description:
        i18n.language === 'mr'
          ? 'Tab, Enter आणि Arrow keys वापरून संपूर्ण वेबसाइट नेव्हिगेट करा'
          : 'Navigate the entire website using Tab, Enter, and Arrow keys',
    },
    {
      icon: Monitor,
      title:
        i18n.language === 'mr'
          ? 'स्क्रीन रीडर समर्थन'
          : 'Screen Reader Support',
      description:
        i18n.language === 'mr'
          ? 'JAWS, NVDA आणि इतर screen readers सोबत पूर्ण सुसंगतता'
          : 'Full compatibility with JAWS, NVDA, and other screen readers',
    },
    {
      icon: Smartphone,
      title:
        i18n.language === 'mr'
          ? 'मोबाइल सुलभ वापर सुविधा'
          : 'Mobile Accessibility',
      description:
        i18n.language === 'mr'
          ? 'सर्व मोबाइल उपकरणांवर responsive आणि accessible डिझाइन'
          : 'Responsive and accessible design across all mobile devices',
    },
    {
      icon: MousePointer,
      title: i18n.language === 'mr' ? 'माउस पर्याय' : 'Mouse Alternatives',
      description:
        i18n.language === 'mr'
          ? 'माउसशिवाय सर्व कार्ये वापरणे शक्य'
          : 'All functions accessible without requiring mouse interaction',
    },
  ];

  const wcagGuidelines = [
    {
      title: i18n.language === 'mr' ? 'दृश्यमान (Perceivable)' : 'Perceivable',
      points:
        i18n.language === 'mr'
          ? [
              'सभी images के लिए alt text प्रदान किया गया',
              'उचित color contrast ratio बनाए रखा गया',
              'Text को 200% तक zoom किया जा सकता है',
              'Color के अलावा अन्य indicators भी उपलब्ध',
            ]
          : [
              'Alt text provided for all images',
              'Proper color contrast ratios maintained',
              'Text can be zoomed up to 200%',
              'Information conveyed through means other than color',
            ],
    },
    {
      title: i18n.language === 'mr' ? 'संचालित (Operable)' : 'Operable',
      points:
        i18n.language === 'mr'
          ? [
              'सभी functions keyboard से accessible हैं',
              'कोई flashing content नहीं है',
              'उपयोगकर्ताओं को navigation में मदद मिलती है',
              'पर्याप्त समय content पढ़ने के लिए दिया गया',
            ]
          : [
              'All functionality accessible via keyboard',
              'No flashing content that causes seizures',
              'Users can navigate and find content',
              'Users have enough time to read content',
            ],
    },
    {
      title:
        i18n.language === 'mr'
          ? 'समझने योग्य (Understandable)'
          : 'Understandable',
      points:
        i18n.language === 'mr'
          ? [
              'Text readable और समझने योग्य है',
              'Web pages predictable तरीके से काम करते हैं',
              'Users को errors के बारे में जानकारी दी जाती है',
              'Input fields में clear labels हैं',
            ]
          : [
              'Text is readable and understandable',
              'Web pages appear and operate predictably',
              'Users are helped to avoid and correct mistakes',
              'Clear labels provided for input fields',
            ],
    },
    {
      title: i18n.language === 'mr' ? 'मजबूत (Robust)' : 'Robust',
      points:
        i18n.language === 'mr'
          ? [
              'Content को विभिन्न assistive technologies द्वारा interpreted किया जा सकता है',
              'Valid HTML और CSS का उपयोग',
              'Modern web standards का पालन',
              'Cross-browser compatibility',
            ]
          : [
              'Content can be interpreted by assistive technologies',
              'Valid HTML and CSS used',
              'Follows modern web standards',
              'Cross-browser compatibility maintained',
            ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      {/* Header */}
      <div
        className="w-full bg-white border-b-4 shadow-lg"
        style={{ borderBottomColor: '#B8860B' }}
      >
        <div
          className="w-full h-2"
          style={{
            background:
              'linear-gradient(to right, #FF6600 0%, #FFD700 50%, #FF6600 100%)',
          }}
        ></div>

        <div className="py-6 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {i18n.language === 'mr' ? 'सुलभ वापर सुविधा' : 'Web Accessibility'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'mr'
                ? 'गडचिरोली जिल्हा नागरिक तक्रार व्यवस्थापन प्रणाली - सर्वांसाठी सुलभ'
                : 'Gadchiroli District Citizen Complaint Management System - Accessible to All'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-8 lg:px-16">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <Link
                  to="/"
                  className="hover:text-gray-800 transition-colors"
                  style={{ color: '#B8860B' }}
                >
                  {i18n.language === 'mr' ? 'मुख्यपृष्ठ' : 'Home'}
                </Link>
              </li>
              <li>/</li>
              <li className="font-medium text-gray-800">
                {i18n.language === 'mr' ? 'सुलभ वापर सुविधा' : 'Accessibility'}
              </li>
            </ol>
          </nav>

          {/* Introduction */}
          <div
            className="mb-8 p-6 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
            }}
          >
            <div className="text-white">
              <h2 className="text-xl font-bold mb-3">
                {i18n.language === 'mr'
                  ? 'हमारी प्रतिबद्धता'
                  : 'Our Commitment'}
              </h2>
              <p className="text-yellow-100 leading-relaxed">
                {i18n.language === 'mr'
                  ? 'गडचिरोली जिला नागरिक तक्रार व्यवस्थापन प्रणाली सभी नागरिकों के लिए सुलभ है, चाहे उनकी शारीरिक क्षमताएं कुछ भी हों। हमने इस वेबसाइट को WCAG 2.1 AA मानकों के अनुसार डिज़ाइन किया है।'
                  : 'The Gadchiroli District Citizen Complaint Management System is designed to be accessible to all citizens, regardless of their physical abilities. This website has been developed following WCAG 2.1 AA accessibility standards.'}
              </p>
            </div>
          </div>

          {/* Accessibility Features */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <CheckCircle
                className="w-6 h-6 mr-3"
                style={{ color: '#B8860B' }}
              />
              {i18n.language === 'mr'
                ? 'सुलभता विशेषताएं'
                : 'Accessibility Features'}
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {accessibilityFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-center mb-4">
                      <IconComponent
                        className="w-8 h-8 mr-3"
                        style={{ color: '#B8860B' }}
                      />
                      <h3 className="text-lg font-semibold text-gray-800">
                        {feature.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* WCAG Guidelines */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Settings className="w-6 h-6 mr-3" style={{ color: '#B8860B' }} />
              {i18n.language === 'mr'
                ? 'WCAG 2.1 अनुपालन'
                : 'WCAG 2.1 Compliance'}
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {wcagGuidelines.map((guideline, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-6"
                >
                  <h3
                    className="text-xl font-bold text-gray-800 mb-4"
                    style={{ color: '#B8860B' }}
                  >
                    {guideline.title}
                  </h3>
                  <ul className="space-y-2">
                    {guideline.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start">
                        <CheckCircle className="w-4 h-4 mr-2 mt-1 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Keyboard className="w-6 h-6 mr-3" style={{ color: '#B8860B' }} />
              {i18n.language === 'mr'
                ? 'कीबोर्ड शॉर्टकट्स'
                : 'Keyboard Shortcuts'}
            </h2>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">
                    {i18n.language === 'mr' ? 'नेविगेशन' : 'Navigation'}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">Tab</kbd> -{' '}
                      {i18n.language === 'mr'
                        ? 'अगले element पर जाएं'
                        : 'Move to next element'}
                    </li>
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">
                        Shift + Tab
                      </kbd>{' '}
                      -{' '}
                      {i18n.language === 'mr'
                        ? 'पिछले element पर जाएं'
                        : 'Move to previous element'}
                    </li>
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">Enter</kbd>{' '}
                      -{' '}
                      {i18n.language === 'mr'
                        ? 'link या button activate करें'
                        : 'Activate link or button'}
                    </li>
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">Esc</kbd> -{' '}
                      {i18n.language === 'mr'
                        ? 'modal या dropdown बंद करें'
                        : 'Close modal or dropdown'}
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">
                    {i18n.language === 'mr'
                      ? 'फॉर्म नेविगेशन'
                      : 'Form Navigation'}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">
                        Arrow Keys
                      </kbd>{' '}
                      -{' '}
                      {i18n.language === 'mr'
                        ? 'radio buttons और dropdown में navigate करें'
                        : 'Navigate radio buttons and dropdowns'}
                    </li>
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">Space</kbd>{' '}
                      -{' '}
                      {i18n.language === 'mr'
                        ? 'checkbox toggle करें'
                        : 'Toggle checkbox'}
                    </li>
                    <li>
                      <kbd className="bg-gray-200 px-2 py-1 rounded">
                        Ctrl + A
                      </kbd>{' '}
                      -{' '}
                      {i18n.language === 'mr'
                        ? 'सभी text select करें'
                        : 'Select all text'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Browser and Assistive Technology Support */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Monitor className="w-6 h-6 mr-3" style={{ color: '#B8860B' }} />
              {i18n.language === 'mr'
                ? 'समर्थित तकनीक'
                : 'Supported Technology'}
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {i18n.language === 'mr'
                    ? 'समर्थित ब्राउज़र'
                    : 'Supported Browsers'}
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Google Chrome (latest version)</li>
                  <li>• Mozilla Firefox (latest version)</li>
                  <li>• Microsoft Edge (latest version)</li>
                  <li>• Safari (latest version)</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {i18n.language === 'mr'
                    ? 'सहायक तकनीक'
                    : 'Assistive Technology'}
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• JAWS Screen Reader</li>
                  <li>• NVDA Screen Reader</li>
                  <li>• VoiceOver (Mac/iOS)</li>
                  <li>• TalkBack (Android)</li>
                  <li>• Dragon Speech Recognition</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-8 text-center text-sm text-gray-500">
            {i18n.language === 'mr'
              ? `पिछला अपडेट: ${new Date().toLocaleDateString('hi-IN')}`
              : `Last Updated: ${new Date().toLocaleDateString('en-US')}`}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityPage;
